import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Upload, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const MAX_BYTES = 10_000_000;
const MAX_EDGE = 2000;

/** Downscale big camera/screenshot photos in the browser so they clear the
 *  10MB ceiling instead of failing the upload. Returns the original file if it
 *  is already small or cannot be decoded. */
async function shrinkIfNeeded(file) {
  if (!file.type.startsWith("image/")) return file;
  if (file.size <= 3_500_000) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise(res => canvas.toBlob(res, "image/jpeg", 0.85));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch {
    return file;
  }
}

export default function UploadButton({ onUploaded, label = "Upload image", className = "", accept = "image/*" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const handle = async (e) => {
    const picked = e.target.files?.[0];
    if (ref.current) ref.current.value = "";
    if (!picked) return;
    setError("");
    setBusy(true);
    try {
      const file = await shrinkIfNeeded(picked);
      if (file.size > MAX_BYTES) {
        throw new Error("That photo is larger than 10MB even after resizing. Try a smaller photo.");
      }
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded?.(`${process.env.REACT_APP_BACKEND_URL}${data.url}`, data.path);
      toast.success("Photo attached");
    } catch (err) {
      const reason = err.response?.data?.detail || err.message || "The upload didn't go through.";
      setError(`Photo not attached — ${reason} Your text is still here, so you can retry or post without a photo.`);
      toast.error("Photo not attached");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={handle} data-testid="upload-input"/>
      <div>
        <button type="button" onClick={() => ref.current?.click()} disabled={busy}
          className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-full border border-black/10 bg-white hover:bg-black/5 disabled:opacity-50 ${className}`}
          data-testid="upload-btn">
          {busy ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>}
          {busy ? "Uploading…" : label}
        </button>
        {error && (
          <p className="mt-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg px-2.5 py-2 flex items-start gap-1.5 max-w-xs" data-testid="upload-error">
            <AlertTriangle size={13} className="shrink-0 mt-0.5"/> <span>{error}</span>
          </p>
        )}
      </div>
    </>
  );
}
